import { useState, useMemo, useCallback } from "react";
import Data from "../../../apiData/yoga.json";

// ─── Build a flat index across all 4 levels ────────────────────────────────────
// Structure: category → subcat → subSubCat(group) → subSubCat(videos)
// We index every leaf video that has an asnVideoPath.
// Duplicates (same videoPath) are deduplicated — first occurrence wins.

const _rawIndex = Data.flatMap((cat) =>
  (cat.subcat ?? []).flatMap((sub) =>
    (sub.subSubCat ?? []).flatMap((group) => {
      // A group entry can itself be a video (has asnVideoPath but no child subSubCat)
      // OR it can be a container with its own subSubCat array of videos.
      const children = group.subSubCat ?? [];

      if (children.length === 0 && group.asnVideoPath) {
        // Leaf video at level 3
        return [{
          videoPath:   group.asnVideoPath,
          sN:          group.sN,
          name:        group.subCatName,
          duration:    group.asnVideoDuration ?? "",
          description: group.description ?? "",
          tags: (group.asnTags ?? "")
            .split(",").map((t) => t.trim()).filter(Boolean),
          rawTags:     group.asnTags ?? "",
          category:    cat.category,
          subCatName:  sub.subCatName,
          groupName:   group.subCatName,
          // Carry the full node so the player gets all fields
          node: group,
        }];
      }

      // Container group — index each child video
      return children
        .filter((v) => v.asnVideoPath)
        .map((v) => ({
          videoPath:   v.asnVideoPath,
          sN:          v.sN,
          name:        v.subCatName,
          duration:    v.asnVideoDuration ?? "",
          description: v.description ?? "",
          tags: (v.asnTags ?? "")
            .split(",").map((t) => t.trim()).filter(Boolean),
          rawTags:     v.asnTags ?? "",
          category:    cat.category,
          subCatName:  sub.subCatName,
          groupName:   group.subCatName,
          node: v,
        }));
    })
  )
);

// Deduplicate by videoPath
const YOG_INDEX = [
  ...new Map(_rawIndex.map((a) => [a.videoPath, a])).values(),
];

// All unique, non-empty tags sorted alphabetically
export const YOG_ALL_TAGS = [
  ...new Map(
    YOG_INDEX.flatMap((a) => a.tags).map((t) => [t.toLowerCase(), t])
  ).values(),
].sort();

/**
 * useYogSearch
 *
 * Tag + free-text search across the full 4-level yoga.json hierarchy.
 * Returns the same interface shape as useAsanaSearch so <SearchPanel> is reusable.
 *
 * @param {object} analytics  - useYogAnalytics() return value, passed in from Yog.js
 *                              so this hook doesn't need to instantiate it separately.
 */
const useYogSearch = (analytics = {}) => {
  const [query,     setQueryRaw]  = useState("");
  const [activeTag, setActiveTag] = useState(null);

  const normQuery  = useMemo(() => query.trim().toLowerCase(), [query]);
  const isSearching = normQuery.length > 0 || activeTag !== null;

  const results = useMemo(() => {
    if (!isSearching) return [];

    return YOG_INDEX.filter((item) => {
      const tagsLower = item.tags.map((t) => t.toLowerCase());

      const tagMatch = activeTag
        ? tagsLower.includes(activeTag.toLowerCase())
        : true;

      const textMatch = normQuery
        ? item.name.toLowerCase().includes(normQuery)       ||
          tagsLower.some((t) => t.includes(normQuery))      ||
          item.category.toLowerCase().includes(normQuery)   ||
          item.subCatName.toLowerCase().includes(normQuery) ||
          item.groupName.toLowerCase().includes(normQuery)
        : true;

      return tagMatch && textMatch;
    });
  }, [normQuery, activeTag, isSearching]);

  // ── Track search queries (debounced inside the analytics hook) ───────────────
  // We fire this in the results useMemo so the resultsCount is always accurate.
  useMemo(() => {
    if (normQuery && analytics.trackSearchQuery) {
      analytics.trackSearchQuery(normQuery, results.length);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normQuery, results.length]);

  const setQuery = useCallback((val) => {
    setQueryRaw(val);
    if (val.trim()) setActiveTag(null);
  }, []);

  const handleTagClick = useCallback((tag) => {
    setActiveTag((prev) => {
      const isActivating = prev !== tag;
      const next = isActivating ? tag : null;
      // Track tag activation (not deactivation)
      if (isActivating && analytics.trackTagFilter) {
        // resultsCount will be stale here by one render, acceptable trade-off
        analytics.trackTagFilter(tag, 0, true);
      }
      return next;
    });
    setQueryRaw("");
  }, [analytics]);

  const clearSearch = useCallback(() => {
    setQueryRaw("");
    setActiveTag(null);
    if (analytics.trackSearchClear) {
      analytics.trackSearchClear();
    }
  }, [analytics]);

  return {
    query,
    activeTag,
    results,
    isSearching,
    setQuery,
    setActiveTag: handleTagClick,
    clearSearch,
  };
};

export default useYogSearch;